import { useCarePlanContext } from "../care-plan";
import { hasTimelineStepAfterDecision } from "../types";

import { ReactNode, useMemo } from "react";

import { cx } from "@/lib/tailwind-utils";

import { useOptionalDecisionStep } from "./decision-step-context";

interface BranchDisplayProps {
  children: ReactNode;
}

/**
 * Visual container for decision branches that always renders,
 * regardless of resolution state. Use this instead of BranchPicker
 * when you want branches visible in read-only / display contexts.
 *
 * Orientation-aware:
 * - vertical: top/bottom bracket with branches side-by-side (same as BranchPicker)
 * - horizontal: tree fork — vertical spine with branches stacked, each flowing right
 */
export function BranchDisplay({ children }: BranchDisplayProps) {
  const {
    state: { steps },
    config: { orientation },
  } = useCarePlanContext();
  const decisionStepId = useOptionalDecisionStep()?.state.decisionStep.id;

  const showRightSpine = useMemo(() => {
    if (!decisionStepId) return true;
    return hasTimelineStepAfterDecision(steps, decisionStepId);
  }, [steps, decisionStepId]);

  if (orientation === "horizontal") {
    return (
      <div className="flex items-stretch">
        <div className="w-4 bg-border h-px shrink-0 self-center" />
        <div className="flex flex-col">{children}</div>
        {showRightSpine && (
          <div className="w-4 bg-border h-px shrink-0 self-center" />
        )}
      </div>
    );
  }

  return (
    <>
      <div className={cx("w-px h-full bg-border", "h-12")} />
      <div className={cx("flex")}>{children}</div>
    </>
  );
}
